import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  apiErrorMessage,
  archiveThread,
  createMessageThread,
  getUnreadNotificationCount,
  listJobs,
  listJobMessageRecipients,
  listJobShifts,
  listLocationMessageRecipients,
  listMessageThreads,
  listShiftMessageRecipients,
  listThreadMessages,
  markThreadRead,
  searchMessageRecipients,
  sendThreadMessage,
} from '../../../services/access'
import { PUNCH_NAVIGATE_EVENT, navigateTo } from '../../../services/navigation'
import type { AccessibleLocation, CreateMessageThreadRequest, Job, JobShift, Message, MessageRecipient, MessageThread, WorkerOrgRelationship } from '../../../types/access'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'
import { useI18n } from '../../../i18n/I18nContext'

type MessagesPanelProps = {
  accessibleLocations: AccessibleLocation[]
  workerRelationships: WorkerOrgRelationship[]
  selectedOrgId: string
  selectedLocationId: string
  canViewMessages: boolean
  canSendMessages: boolean
  refreshVersion?: number
}

type FilterKey = 'all' | 'unread' | 'direct' | 'groups' | 'job' | 'shift' | 'location'

const filters: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'direct', label: 'Direct' },
  { key: 'groups', label: 'Groups' },
  { key: 'job', label: 'Job' },
  { key: 'shift', label: 'Shift' },
  { key: 'location', label: 'Location' },
]

const threadTitle = (thread: MessageThread | null | undefined, t: (text: string, vars?: Record<string, string | number>) => string, workerLabel: string) => {
  if (!thread) return t('Select a conversation')
  if (thread.title) return thread.title
  const others = thread.participants?.filter((participant) => participant.participantRole === 'worker') || []
  if (others.length === 1) return others[0].displayName || others[0].email || workerLabel
  if (others.length > 1) return t('{count} workers', { count: others.length })
  return t(readableThreadType(thread.threadType))
}

const recipientLabel = (recipient: MessageRecipient) => recipient.displayName || recipient.email || recipient.phone || 'Eligible worker'
const readableThreadType = (value?: string | null) => value ? value.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase()) : 'Thread'
const threadKindLabel = (thread: Pick<MessageThread, 'threadType' | 'scopeType'> | null | undefined, t: (text: string) => string) => {
  if (!thread) return t('Thread')
  if (thread.scopeType === 'job' || thread.threadType === 'job_thread') return t('Job')
  if (thread.scopeType === 'shift' || thread.threadType === 'shift_thread') return t('Shift')
  if (thread.scopeType === 'location' || thread.threadType === 'location_thread') return t('Location')
  if (thread.threadType === 'admin_group' || thread.threadType === 'broadcast') return t('Group')
  return t('Direct')
}
const replyPolicyLabel = (thread: MessageThread | null | undefined, t: (text: string) => string) => !thread ? t('No thread selected') : thread.replyPolicy === 'admins_only' ? t('Announcement only') : t('Replies enabled')
const readableStatus = (value?: string | null) => value ? value.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase()) : ''
const threadIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const unavailableThreadMessage = 'This conversation is unavailable or you no longer have access.'
const jobLabel = (job: Job) => job.title || job.company_name || job.company_org_name || 'Untitled job'
const compactParts = (parts: Array<string | null | undefined>) => parts.map((part) => part?.trim()).filter(Boolean).join(' . ')
const shiftBelongsToJob = (shift: JobShift | null, job: Job | null) => Boolean(shift && job && shift.job_id === job.id)
const areSameJsonRows = <T,>(left: T[], right: T[]) => JSON.stringify(left) === JSON.stringify(right)
let preservedMessageDraft = ''

const readRequestedThreadId = () => {
  if (typeof window === 'undefined') return ''
  return (new URLSearchParams(window.location.search).get('threadId') || '').trim()
}

const messagesUrl = (threadId?: string | null) => {
  if (typeof window === 'undefined') return '/admin?workspace=messages'
  const url = new URL(window.location.href)
  const params = url.searchParams
  params.set('workspace', 'messages')
  if (threadId?.trim()) params.set('threadId', threadId.trim())
  else params.delete('threadId')
  const search = params.toString()
  return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
}

const updateMessageThreadUrl = (threadId: string | null, replace = false, notify = true) => {
  if (typeof window === 'undefined') return
  navigateTo(messagesUrl(threadId), { replace, notify })
}

export const MessagesPanel: React.FC<MessagesPanelProps> = ({
  accessibleLocations,
  selectedOrgId,
  selectedLocationId,
  canViewMessages,
  canSendMessages,
  refreshVersion = 0,
}) => {
  const industryLabels = useIndustryTerminology()
  const { t, formatDateTime: localizedDateTime, formatTime: localizedTime } = useI18n()
  const [threads, setThreads] = useState<MessageThread[]>([])
  const [selectedThreadId, setSelectedThreadId] = useState('')
  const [requestedThreadId, setRequestedThreadId] = useState(() => readRequestedThreadId())
  const [threadsLoaded, setThreadsLoaded] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [draft, setDraftState] = useState(preservedMessageDraft)
  const [initialLoading, setInitialLoading] = useState(false)
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [notificationUnreadCount, setNotificationUnreadCount] = useState<number | null>(null)
  const [notificationUnavailable, setNotificationUnavailable] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [recipientSearch, setRecipientSearch] = useState('')
  const [recipients, setRecipients] = useState<MessageRecipient[]>([])
  const [recipientsLoading, setRecipientsLoading] = useState(false)
  const [recipientsError, setRecipientsError] = useState('')
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([])
  const [newType, setNewType] = useState<CreateMessageThreadRequest['threadType']>('direct_admin_worker')
  const [newTitle, setNewTitle] = useState('')
  const [replyPolicy, setReplyPolicy] = useState<CreateMessageThreadRequest['replyPolicy']>('participants_can_reply')
  const [scopeKind, setScopeKind] = useState<'none' | 'location' | 'job' | 'shift'>('none')
  const [scopeId, setScopeId] = useState('')
  const [jobSearch, setJobSearch] = useState('')
  const [jobs, setJobs] = useState<Job[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobsError, setJobsError] = useState('')
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [shifts, setShifts] = useState<JobShift[]>([])
  const [shiftsLoading, setShiftsLoading] = useState(false)
  const [shiftsError, setShiftsError] = useState('')
  const [selectedShift, setSelectedShift] = useState<JobShift | null>(null)
  const [firstMessage, setFirstMessage] = useState('')
  const [unavailableThreadNotice, setUnavailableThreadNotice] = useState('')
  const loadingThreadsRef = useRef(false)
  const loadingMessagesRef = useRef(false)
  const threadLoadSeqRef = useRef(0)
  const messageLoadSeqRef = useRef(0)
  const jobSearchSeqRef = useRef(0)
  const shiftLoadSeqRef = useRef(0)
  const requestedThreadIdRef = useRef(requestedThreadId)
  const searchRef = useRef(search)
  const filterRef = useRef(filter)
  const threadsLoadedRef = useRef(threadsLoaded)
  const threadsRef = useRef(threads)
  const searchEffectInitializedRef = useRef(false)
  const lastThreadScopeRef = useRef(`${selectedOrgId || ''}:${selectedLocationId || ''}`)

  const selectedThread = useMemo(() => threads.find((thread) => thread.id === selectedThreadId) || null, [threads, selectedThreadId])
  const threadUnreadCount = useMemo(() => threads.reduce((total, thread) => total + (Number(thread.unreadCount) || 0), 0), [threads])
  const visibleUnreadCount = notificationUnreadCount ?? threadUnreadCount
  const selectedThreadContext = useMemo(() => {
    if (!selectedThread) return ''
    const parts = [
      threadKindLabel(selectedThread, t),
      replyPolicyLabel(selectedThread, t),
      selectedThread.status !== 'active' ? t(readableStatus(selectedThread.status)) : null,
      selectedThread.unreadCount ? t('{count} unread', { count: selectedThread.unreadCount }) : null,
    ]
    return compactParts(parts)
  }, [selectedThread, t])
  const setDraft = useCallback((value: string) => {
    preservedMessageDraft = value
    setDraftState(value)
  }, [])

  useEffect(() => { requestedThreadIdRef.current = requestedThreadId }, [requestedThreadId])
  useEffect(() => { searchRef.current = search }, [search])
  useEffect(() => { filterRef.current = filter }, [filter])
  useEffect(() => { threadsLoadedRef.current = threadsLoaded }, [threadsLoaded])
  useEffect(() => { threadsRef.current = threads }, [threads])

  const loadThreads = useCallback(async (options: { background?: boolean } = {}) => {
    if (!canViewMessages) return
    if (loadingThreadsRef.current) return
    const sequence = threadLoadSeqRef.current + 1
    threadLoadSeqRef.current = sequence
    loadingThreadsRef.current = true
    const isBackground = Boolean(options.background && threadsLoadedRef.current)
    if (isBackground) setBackgroundRefreshing(true)
    else setInitialLoading(true)
    setError('')
    try {
      const [rows, notificationCounts] = await Promise.all([
        listMessageThreads({ search: searchRef.current, filter: filterRef.current }),
        getUnreadNotificationCount().catch(() => null),
      ])
      if (sequence !== threadLoadSeqRef.current) return
      setThreads((current) => areSameJsonRows(current, rows) ? current : rows)
      setThreadsLoaded(true)
      setNotificationUnreadCount(notificationCounts?.messageUnreadCount ?? null)
      setNotificationUnavailable(!notificationCounts)
      setSelectedThreadId((current) => {
        if (requestedThreadIdRef.current) return current && rows.some((thread) => thread.id === current) ? current : ''
        return current && rows.some((thread) => thread.id === current) ? current : rows[0]?.id || ''
      })
    } catch (err) {
      if (sequence !== threadLoadSeqRef.current) return
      setThreadsLoaded(true)
      if (!isBackground) setError(apiErrorMessage(err, t('Unable to load messages.')))
    } finally {
      if (sequence === threadLoadSeqRef.current) {
        loadingThreadsRef.current = false
        setInitialLoading(false)
        setBackgroundRefreshing(false)
      }
    }
  }, [canViewMessages, t])

  const loadThreadMessages = useCallback(async (threadId: string, options: { background?: boolean } = {}) => {
    if (!threadId || (options.background && loadingMessagesRef.current)) return
    const sequence = messageLoadSeqRef.current + 1
    messageLoadSeqRef.current = sequence
    loadingMessagesRef.current = true
    const unreadBeforeRead = threadsRef.current.find((thread) => thread.id === threadId)?.unreadCount || 0
    const isBackground = Boolean(options.background)
    if (!isBackground) setMessagesLoading(true)
    setError('')
    try {
      const rows = await listThreadMessages(threadId)
      if (sequence !== messageLoadSeqRef.current) return
      setMessages((current) => areSameJsonRows(current, rows) ? current : rows)
      await markThreadRead(threadId)
      if (sequence !== messageLoadSeqRef.current) return
      setThreads((current) => current.map((thread) => thread.id === threadId ? { ...thread, unreadCount: 0 } : thread))
      setNotificationUnreadCount((current) => current == null ? current : Math.max(0, current - unreadBeforeRead))
    } catch (err) {
      if (sequence !== messageLoadSeqRef.current) return
      if (!isBackground) setError(apiErrorMessage(err, t('Unable to load conversation.')))
    } finally {
      if (sequence === messageLoadSeqRef.current) {
        loadingMessagesRef.current = false
        if (!isBackground) setMessagesLoading(false)
      }
    }
  }, [t])

  const loadRecipients = async () => {
    if (!showNew) return
    setRecipientsLoading(true)
    setRecipientsError('')
    try {
      const resolvedScopeId = scopeKind === 'location' ? (scopeId || selectedLocationId) : scopeKind === 'job' ? selectedJob?.id || '' : scopeKind === 'shift' ? selectedShift?.id || '' : scopeId
      const rows = scopeKind === 'location' && resolvedScopeId
        ? await listLocationMessageRecipients(resolvedScopeId)
        : scopeKind === 'job' && resolvedScopeId
          ? await listJobMessageRecipients(resolvedScopeId)
          : scopeKind === 'shift' && selectedShift?.id
            ? await listShiftMessageRecipients(selectedShift.id)
            : scopeKind === 'shift' && selectedJob?.id
              ? await listJobMessageRecipients(selectedJob.id)
            : await searchMessageRecipients(recipientSearch)
      setRecipients(rows)
    } catch (err) {
      setRecipientsError(apiErrorMessage(err, industryLabels.explicitIndustry ? t('Unable to load eligible {workers}.', { workers: industryLabels.label('workers').toLowerCase() }) : t('Unable to load eligible workers.')))
      setRecipients([])
    } finally {
      setRecipientsLoading(false)
    }
  }

  useEffect(() => {
    const scopeKey = `${selectedOrgId || ''}:${selectedLocationId || ''}`
    const scopeChanged = lastThreadScopeRef.current !== scopeKey
    lastThreadScopeRef.current = scopeKey
    if (scopeChanged) {
      setThreadsLoaded(false)
      setThreads([])
      setMessages([])
      setSelectedThreadId('')
      setRequestedThreadId(readRequestedThreadId())
      setUnavailableThreadNotice('')
    }
    const timer = window.setTimeout(() => {
      loadThreads({ background: !scopeChanged && threadsLoadedRef.current })
    }, 0)
    return () => window.clearTimeout(timer)
  }, [canViewMessages, filter, loadThreads, selectedLocationId, selectedOrgId])

  useEffect(() => {
    if (!searchEffectInitializedRef.current) {
      searchEffectInitializedRef.current = true
      return
    }
    const timer = window.setTimeout(() => loadThreads({ background: threadsLoadedRef.current }), 250)
    return () => window.clearTimeout(timer)
  }, [loadThreads, search])

  useEffect(() => {
    if (!refreshVersion || !threadsLoaded) return
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    loadThreads({ background: true })
    if (selectedThreadId) loadThreadMessages(selectedThreadId, { background: true })
  }, [loadThreadMessages, loadThreads, refreshVersion, selectedThreadId, threadsLoaded])

  useEffect(() => {
    if (!threadsLoaded) return undefined
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return
      loadThreads({ background: true })
      if (selectedThreadId) loadThreadMessages(selectedThreadId, { background: true })
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible)
  }, [loadThreadMessages, loadThreads, selectedThreadId, threadsLoaded])
  useEffect(() => { loadRecipients() }, [showNew, recipientSearch, scopeKind, scopeId, selectedJob?.id, selectedShift?.id])

  useEffect(() => {
    if (!showNew) return
    if (scopeKind !== 'job' && scopeKind !== 'shift') return
    const sequence = jobSearchSeqRef.current + 1
    jobSearchSeqRef.current = sequence
    setJobsLoading(true)
    setJobsError('')
    const timer = window.setTimeout(async () => {
      try {
        const rows = await listJobs({ search: jobSearch, limit: 12, sortBy: 'created_at', sortDir: 'desc' })
        if (sequence !== jobSearchSeqRef.current) return
        setJobs(rows)
      } catch (err) {
        if (sequence !== jobSearchSeqRef.current) return
        setJobs([])
        setJobsError(apiErrorMessage(err, t('Unable to load visible jobs.')))
      } finally {
        if (sequence === jobSearchSeqRef.current) setJobsLoading(false)
      }
    }, 250)
    return () => window.clearTimeout(timer)
  }, [showNew, scopeKind, jobSearch, t])

  useEffect(() => {
    if (!showNew || scopeKind !== 'shift' || !selectedJob) {
      setShifts([])
      setShiftsLoading(false)
      setShiftsError('')
      return
    }
    const sequence = shiftLoadSeqRef.current + 1
    shiftLoadSeqRef.current = sequence
    setShiftsLoading(true)
    setShiftsError('')
    listJobShifts(selectedJob.id, { pageSize: 50, sortBy: 'start_date', sortDir: 'asc' })
      .then((rows) => {
        if (sequence !== shiftLoadSeqRef.current) return
        setShifts(rows)
        setSelectedShift((current) => current && rows.some((shift) => shift.id === current.id && shift.job_id === selectedJob.id) ? current : null)
      })
      .catch((err) => {
        if (sequence !== shiftLoadSeqRef.current) return
        setShifts([])
        setSelectedShift(null)
        setShiftsError(apiErrorMessage(err, t('Unable to load visible shifts for this job.')))
      })
      .finally(() => {
        if (sequence === shiftLoadSeqRef.current) setShiftsLoading(false)
      })
  }, [showNew, scopeKind, selectedJob?.id, t])

  useEffect(() => {
    const syncRequestedThread = () => {
      setRequestedThreadId(readRequestedThreadId())
    }
    window.addEventListener('popstate', syncRequestedThread)
    window.addEventListener(PUNCH_NAVIGATE_EVENT, syncRequestedThread)
    return () => {
      window.removeEventListener('popstate', syncRequestedThread)
      window.removeEventListener(PUNCH_NAVIGATE_EVENT, syncRequestedThread)
    }
  }, [])

  useEffect(() => {
    if (!threadsLoaded) return
    const fallbackThreadId = threads[0]?.id || ''
    if (!requestedThreadId) {
      setSelectedThreadId((current) => current === fallbackThreadId ? current : fallbackThreadId)
      return
    }
    if (!threadIdPattern.test(requestedThreadId)) {
      setUnavailableThreadNotice(t(unavailableThreadMessage))
      updateMessageThreadUrl(null, true, false)
      setRequestedThreadId('')
      setSelectedThreadId((current) => current && threads.some((thread) => thread.id === current) ? current : fallbackThreadId)
      return
    }
    const requestedThread = threads.find((thread) => thread.id === requestedThreadId)
    if (!requestedThread) {
      setUnavailableThreadNotice(t(unavailableThreadMessage))
      updateMessageThreadUrl(null, true, false)
      setRequestedThreadId('')
      setSelectedThreadId((current) => current && threads.some((thread) => thread.id === current) ? current : fallbackThreadId)
      return
    }
    setUnavailableThreadNotice('')
    setSelectedThreadId((current) => current === requestedThread.id ? current : requestedThread.id)
  }, [requestedThreadId, t, threads, threadsLoaded])

  useEffect(() => {
    if (!selectedThreadId) {
      setMessages([])
      return
    }
    loadThreadMessages(selectedThreadId)
  }, [loadThreadMessages, selectedThreadId])

  const selectThread = (threadId: string) => {
    if (!threadId || threadId === selectedThreadId) {
      if (threadId && readRequestedThreadId() !== threadId) updateMessageThreadUrl(threadId)
      return
    }
    setUnavailableThreadNotice('')
    setRequestedThreadId(threadId)
    setSelectedThreadId(threadId)
    updateMessageThreadUrl(threadId)
  }

  const send = async () => {
    if (!selectedThread || !draft.trim()) return
    if (sending) return
    setSending(true)
    setError('')
    const optimistic: Message = {
      id: `pending-${Date.now()}`,
      threadId: selectedThread.id,
      senderUserId: 'me',
      senderName: t('You'),
      body: draft.trim(),
      createdAt: new Date().toISOString(),
    }
    setMessages((current) => [...current, optimistic])
    setDraft('')
    try {
      const saved = await sendThreadMessage(selectedThread.id, { body: optimistic.body })
      setMessages((current) => current.map((message) => message.id === optimistic.id ? saved : message))
      await loadThreads({ background: true })
    } catch (err) {
      setMessages((current) => current.filter((message) => message.id !== optimistic.id))
      setError(apiErrorMessage(err, t('Unable to send message.')))
    } finally {
      setSending(false)
    }
  }

  const createThread = async () => {
    if (sending) return
    if (scopeKind === 'job' && !selectedJob) {
      setError(t('Select a visible job before creating a job thread.'))
      return
    }
    if (scopeKind === 'shift' && (!selectedJob || !selectedShift || !shiftBelongsToJob(selectedShift, selectedJob))) {
      setError(t('Select a visible job and one of its shifts before creating a shift thread.'))
      return
    }
    if (!selectedRecipients.length) {
      setError(industryLabels.explicitIndustry ? t('Select at least one eligible {worker}.', { worker: industryLabels.label('worker').toLowerCase() }) : t('Select at least one eligible worker.'))
      return
    }
    if (!firstMessage.trim()) return
    setSending(true)
    setError('')
    const resolvedScopeId = scopeKind === 'location' ? (scopeId || selectedLocationId) : scopeKind === 'job' ? selectedJob?.id || '' : scopeKind === 'shift' ? selectedShift?.id || '' : scopeId
    try {
      const thread = await createMessageThread({
        threadType: newType,
        title: newTitle.trim() || null,
        scopeType: scopeKind === 'none' ? (selectedRecipients.length === 1 ? 'direct' : 'group') : scopeKind,
        scopeId: scopeKind === 'none' ? null : resolvedScopeId || null,
        contextJobId: scopeKind === 'shift' ? selectedJob?.id || null : undefined,
        replyPolicy,
        workerProfileIds: selectedRecipients,
        firstMessageBody: firstMessage.trim(),
      })
      setShowNew(false)
      setSelectedRecipients([])
      setFirstMessage('')
      setNewTitle('')
      setSelectedJob(null)
      setSelectedShift(null)
      setScopeId('')
      setRequestedThreadId(thread.id)
      setSelectedThreadId(thread.id)
      updateMessageThreadUrl(thread.id)
      await loadThreads({ background: true })
      updateMessageThreadUrl(thread.id, true)
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to create message thread.')))
    } finally {
      setSending(false)
    }
  }

  const archiveSelected = async () => {
    if (!selectedThread) return
    await archiveThread(selectedThread.id)
    setStatus(t('Conversation archived.'))
    setRequestedThreadId('')
    updateMessageThreadUrl(null, true)
    await loadThreads({ background: true })
  }

  const resetNewMessage = () => {
    setShowNew(false)
    setRecipientSearch('')
    setRecipients([])
    setRecipientsError('')
    setSelectedRecipients([])
    setNewType('direct_admin_worker')
    setNewTitle('')
    setReplyPolicy('participants_can_reply')
    setScopeKind('none')
    setScopeId('')
    setJobSearch('')
    setJobs([])
    setJobsError('')
    setSelectedJob(null)
    setShifts([])
    setShiftsError('')
    setSelectedShift(null)
    setFirstMessage('')
  }

  const selectMessageScopeKind = (value: 'none' | 'location' | 'job' | 'shift') => {
    setScopeKind(value)
    if (value === 'job') setNewType('job_thread')
    if (value === 'shift') setNewType('shift_thread')
    if (value === 'location') setNewType('location_thread')
    if (value === 'none' && (newType === 'job_thread' || newType === 'shift_thread' || newType === 'location_thread')) setNewType('direct_admin_worker')
    setScopeId('')
    setSelectedJob(null)
    setSelectedShift(null)
    setShifts([])
    setShiftsError('')
    setSelectedRecipients([])
  }

  const selectJob = (job: Job) => {
    setSelectedJob(job)
    setSelectedShift(null)
    setScopeId(job.id)
    setSelectedRecipients([])
    setShifts([])
    setShiftsError('')
  }

  const clearJob = () => {
    setSelectedJob(null)
    setSelectedShift(null)
    setScopeId('')
    setSelectedRecipients([])
    setShifts([])
    setShiftsError('')
  }

  const selectShift = (shift: JobShift) => {
    if (!selectedJob || shift.job_id !== selectedJob.id) return
    setSelectedShift(shift)
    setScopeId(shift.id)
    setSelectedRecipients([])
  }

  const shiftDateLabel = (shift: JobShift) => {
    const start = new Date(shift.starts_at)
    const end = new Date(shift.ends_at)
    if (Number.isNaN(start.getTime())) return shift.starts_at || t('Unscheduled')
    const endLabel = Number.isNaN(end.getTime()) ? '' : ` - ${localizedTime(end)}`
    return `${localizedDateTime(start)}${endLabel}`
  }

  const jobSecondaryLabel = (job: Job) => compactParts([
    job.company_name || job.company_org_name,
    job.location_name,
    readableStatus(job.status),
    job.shift_count != null ? t('{count} shifts', { count: job.shift_count }) : null,
  ])

  if (!canViewMessages) {
    return (
      <article className="panel workspace-panel admin-empty-state stack">
        <h3 style={{ margin: 0 }}>{t('Message access')}</h3>
        <p className="text-muted">{t('Message history is hidden because this admin context does not include message view permission.')}</p>
      </article>
    )
  }

  return (
    <article className="panel workspace-panel stack" aria-busy={backgroundRefreshing || undefined}>
      <div className="admin-panel-toolbar">
        <div>
          <h3 style={{ margin: 0 }}>{t('Conversation inbox')}</h3>
          <p className="text-muted">{industryLabels.explicitIndustry ? t('Conversations with {workers} in your tenant and assigned scope.', { workers: industryLabels.label('workers').toLowerCase() }) : t('Conversations with workers in your tenant and assigned scope.')}</p>
        </div>
        {visibleUnreadCount > 0 ? <span className="status-badge status-badge--warning">{t('{count} unread', { count: visibleUnreadCount })}</span> : null}
        <button type="button" className="btn btn-primary" disabled={!canSendMessages} onClick={() => setShowNew(true)}>{t('New Message')}</button>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      {status ? <p className="helper">{status}</p> : null}
      {unavailableThreadNotice ? <p className="form-error" role="status" data-testid="message-thread-unavailable">{unavailableThreadNotice}</p> : null}
      {visibleUnreadCount > 0 ? (
        <article className="admin-empty-state stack" style={{ borderColor: 'var(--warning)', alignItems: 'flex-start' }}>
          <strong>{t(visibleUnreadCount === 1 ? '{count} unread message' : '{count} unread messages', { count: visibleUnreadCount })}</strong>
          <p className="helper">{notificationUnavailable ? t('Showing thread unread state because notification count is unavailable.') : t('New in-app message notifications are waiting in your inbox.')}</p>
        </article>
      ) : null}
      <div className="admin-messages-layout">
        <aside className="stack admin-messages-list" aria-label={t('Conversation list')}>
          <label className="sr-only" htmlFor="message-thread-search">{t('Search conversations')}</label>
          <input id="message-thread-search" className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('Search conversations')} />
          <div className="admin-tab-list" style={{ flexWrap: 'wrap' }}>
            {filters.map((item) => (
              <button key={item.key} type="button" className={`admin-tab ${filter === item.key ? 'active' : ''}`} onClick={() => setFilter(item.key)}>{t(item.label)}</button>
            ))}
          </div>
          <div className="stack" role="list" aria-label={t('Conversations')}>
            {threads.map((thread) => (
              <button key={thread.id} type="button" className="list-row" style={{ textAlign: 'left', borderColor: selectedThreadId === thread.id ? 'var(--primary)' : undefined }} onClick={() => selectThread(thread.id)} aria-current={selectedThreadId === thread.id ? 'true' : undefined} aria-label={`${threadTitle(thread, t, industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker'))}. ${threadKindLabel(thread, t)}${thread.unreadCount ? `. ${t('{count} unread', { count: thread.unreadCount })}` : ''}`} data-testid={`message-thread-${thread.id}`}>
                <span className="helper">{threadKindLabel(thread, t)} . {replyPolicyLabel(thread, t)}{thread.status !== 'active' ? ` . ${t(readableStatus(thread.status))}` : ''}</span>
                <strong style={{ overflowWrap: 'anywhere' }}>{threadTitle(thread, t, industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker'))}</strong>
                <span className="text-muted admin-message-preview">{thread.lastMessageBody || t('No messages yet')}</span>
                <span className="helper">{localizedDateTime(thread.lastMessageAt)} {thread.unreadCount ? `. ${t('{count} unread', { count: thread.unreadCount })}` : ''}</span>
                {thread.unreadCount ? <span className="status-badge status-badge--warning" style={{ width: 'fit-content' }} aria-label={t('{count} unread', { count: thread.unreadCount })}>{thread.unreadCount}</span> : null}
              </button>
            ))}
            {!threads.length ? <article className="admin-empty-state stack"><strong>{initialLoading ? t('Loading conversations...') : t('No conversations yet')}</strong><p className="helper">{initialLoading ? (industryLabels.explicitIndustry ? t('Fetching the latest {worker} conversations.', { worker: industryLabels.label('worker').toLowerCase() }) : t('Fetching the latest worker conversations.')) : (industryLabels.explicitIndustry ? t('Start a direct, group, job, shift, or location thread when you need to reach {workers}.', { workers: industryLabels.label('workers').toLowerCase() }) : t('Start a direct, group, job, shift, or location thread when you need to reach workers.'))}</p></article> : null}
          </div>
        </aside>
        <section className="stack admin-messages-detail" aria-live="polite" aria-label={selectedThread ? t('Selected conversation') : t('No selected conversation')}>
          <div className="admin-panel-toolbar">
            <div>
              <h4 style={{ margin: 0 }}>{threadTitle(selectedThread, t, industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker'))}</h4>
              <p className="text-muted">{selectedThread ? selectedThreadContext : t('No thread selected')}</p>
            </div>
            <button type="button" className="btn btn-secondary" disabled={!selectedThread} onClick={archiveSelected}>{t('Archive')}</button>
          </div>
          <div className="stack" style={{ flex: 1, minHeight: 360, overflowY: 'auto', padding: '.5rem' }}>
            {messages.map((message) => {
              const mine = message.senderUserId === 'me' || message.senderRole === 'admin' || message.senderRole === 'supervisor'
              return (
                <div key={message.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: 'min(78%, 42rem)', minWidth: 0, background: mine ? 'var(--primary-soft)' : 'var(--surface-muted)', border: '1px solid var(--border)', borderRadius: 8, padding: '.65rem .8rem', overflowWrap: 'anywhere' }}>
                  <strong>{message.senderName || t('User')}</strong>
                  <p style={{ margin: '.25rem 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.body}</p>
                  <span className="helper">{localizedDateTime(message.createdAt)}</span>
                </div>
              )
            })}
            {!messages.length ? <article className="admin-empty-state stack"><strong>{messagesLoading ? t('Loading messages...') : selectedThread ? t('No messages in this conversation') : t('No conversation open')}</strong><p className="helper">{messagesLoading ? t('Fetching thread messages.') : selectedThread ? t('Reply when you have context to share with the selected participants.') : t('Select a thread from the list or create a new message.')}</p></article> : null}
          </div>
          <div className="admin-form-grid">
            <label className="admin-form-wide">{t('Message')}<textarea className="input" rows={3} value={draft} disabled={!selectedThread?.canReply || selectedThread.status !== 'active' || sending} onChange={(event) => setDraft(event.target.value)} aria-describedby="message-compose-help" /></label>
          </div>
          <p id="message-compose-help" className="helper">{selectedThread && (!selectedThread.canReply || selectedThread.status !== 'active') ? (selectedThread.status !== 'active' ? t('This thread is archived or closed.') : t('Announcement only: replies are disabled for your participant role.')) : t('Replies are sent only to active participants in this conversation.')}</p>
          <button type="button" className="btn btn-primary" disabled={!selectedThread?.canReply || selectedThread.status !== 'active' || !draft.trim() || sending} onClick={send}>{t('Send')}</button>
        </section>
        <aside className="stack admin-messages-info" aria-label={t('Conversation context')}>
          <h4 style={{ margin: 0 }}>{t('Thread Info')}</h4>
          <p className="helper">{selectedThread ? `${threadKindLabel(selectedThread, t)} . ${t(readableThreadType(selectedThread.threadType))}` : t('No thread selected')}</p>
          <p className="helper">{t('Reply policy: {policy}', { policy: replyPolicyLabel(selectedThread, t) })}</p>
          {(selectedThread?.participants || []).map((participant) => (
            <div key={participant.id} className="list-row">
              <strong style={{ overflowWrap: 'anywhere' }}>{participant.displayName || participant.email || t('Participant')}</strong>
              <span className="helper">{t(readableStatus(participant.participantRole))}{participant.canReply ? ` . ${t('can reply')}` : ''}</span>
            </div>
          ))}
        </aside>
      </div>
      {showNew ? (
        <div className="modal-backdrop">
          <div className="modal stack" style={{ maxWidth: 760 }}>
            <div className="admin-panel-toolbar">
              <h3 style={{ margin: 0 }}>{t('New Message')}</h3>
              <button type="button" className="btn btn-secondary" onClick={resetNewMessage}>{t('Close')}</button>
            </div>
            <div className="admin-form-grid">
              <label>{t('Type')}<select className="input" value={newType} onChange={(event) => setNewType(event.target.value as CreateMessageThreadRequest['threadType'])}>
                <option value="direct_admin_worker">{industryLabels.explicitIndustry ? t('Direct admin-{worker}', { worker: industryLabels.label('worker').toLowerCase() }) : t('Direct admin-worker')}</option>
                <option value="admin_group">{t('Group')}</option>
                <option value="broadcast">{t('Broadcast')}</option>
                <option value="job_thread">{t('Job')}</option>
                <option value="shift_thread">{t('Shift')}</option>
                <option value="location_thread">{t('Location')}</option>
              </select></label>
              <label>{t('Scope')}<select className="input" value={scopeKind} onChange={(event) => selectMessageScopeKind(event.target.value as any)}>
                <option value="none">{t('Direct/group')}</option>
                <option value="location">{t('Location')}</option>
                <option value="job">{t('Job')}</option>
                <option value="shift">{t('Shift')}</option>
              </select></label>
              {scopeKind === 'location' ? <label>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}<select className="input" value={scopeId || selectedLocationId} onChange={(event) => setScopeId(event.target.value)}>
                <option value="">{industryLabels.explicitIndustry ? t('Select {location}', { location: industryLabels.label('location').toLowerCase() }) : t('Select location')}</option>
                {accessibleLocations.map((location) => <option key={location.id} value={location.id}>{location.name || location.label || t('Visible location')}</option>)}
              </select></label> : null}
              {scopeKind === 'job' || scopeKind === 'shift' ? (
                <div className="admin-form-wide stack">
                  <div className="admin-panel-toolbar" style={{ alignItems: 'flex-end' }}>
                    <label style={{ flex: '1 1 18rem' }}>{t('Related job or order')}
                      <input
                        className="input"
                        value={jobSearch}
                        onChange={(event) => setJobSearch(event.target.value)}
                        placeholder={industryLabels.explicitIndustry ? t('Search visible {jobs}', { jobs: industryLabels.label('jobs').toLowerCase() }) : t('Search visible jobs')}
                        role="combobox"
                        aria-expanded="true"
                        aria-controls="message-job-results"
                        aria-describedby="message-job-help"
                      />
                    </label>
                    {selectedJob ? <button type="button" className="btn btn-secondary" onClick={clearJob}>{t('Clear job')}</button> : null}
                  </div>
                  <p id="message-job-help" className="helper">{t('Adding job context helps participants understand what this conversation concerns.')}</p>
                  <div id="message-job-results" role="listbox" aria-label={t('Visible jobs')} className="stack" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {jobsError ? <p className="form-error" role="status">{jobsError}</p> : null}
                    {jobsLoading ? <p className="helper" role="status">{industryLabels.explicitIndustry ? t('Loading visible {jobs}...', { jobs: industryLabels.label('jobs').toLowerCase() }) : t('Loading visible jobs...')}</p> : null}
                    {jobs.map((job) => (
                      <button
                        key={job.id}
                        type="button"
                        role="option"
                        aria-selected={selectedJob?.id === job.id}
                        className="list-row"
                        style={{ textAlign: 'left', borderColor: selectedJob?.id === job.id ? 'var(--primary)' : undefined }}
                        onClick={() => selectJob(job)}
                      >
                        <strong style={{ overflowWrap: 'anywhere' }}>{jobLabel(job)}</strong>
                        <span className="helper">{jobSecondaryLabel(job) || t('No additional job context available')}</span>
                      </button>
                    ))}
                    {!jobs.length && !jobsLoading && !jobsError ? <p className="helper">{jobSearch ? t('No visible jobs match your search.') : t('No visible jobs found.')}</p> : null}
                  </div>
                </div>
              ) : null}
              {scopeKind === 'shift' ? (
                <div className="admin-form-wide stack">
                  <div className="admin-panel-toolbar" style={{ alignItems: 'flex-end' }}>
                    <label style={{ flex: '1 1 18rem' }}>{t('Related shift (optional)')}
                      <select
                        className="input"
                        value={selectedShift?.id || ''}
                        disabled={!selectedJob || shiftsLoading}
                        onChange={(event) => {
                          const shift = shifts.find((item) => item.id === event.target.value) || null
                          setSelectedShift(null)
                          setScopeId('')
                          if (shift) selectShift(shift)
                        }}
                      >
                        <option value="">{selectedJob ? t('Select a shift') : t('Select a job first')}</option>
                        {shifts.map((shift) => (
                          <option key={shift.id} value={shift.id}>
                            {compactParts([shiftDateLabel(shift), shift.location_name || selectedJob?.location_name, readableStatus(shift.status)])}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selectedShift ? <button type="button" className="btn btn-secondary" onClick={() => { setSelectedShift(null); setScopeId('') }}>{t('Clear shift')}</button> : null}
                  </div>
                  {shiftsLoading ? <p className="helper" role="status">{t('Loading visible shifts for this job...')}</p> : null}
                  {shiftsError ? <p className="form-error" role="status">{shiftsError}</p> : null}
                  {selectedJob && !shifts.length && !shiftsLoading && !shiftsError ? <p className="helper">{t('This job has no visible shifts in your current scope.')}</p> : null}
                </div>
              ) : null}
              <label>{t('Reply policy')}<select className="input" value={replyPolicy} onChange={(event) => setReplyPolicy(event.target.value as CreateMessageThreadRequest['replyPolicy'])}>
                <option value="participants_can_reply">{t('Participants can reply')}</option>
                <option value="admins_only">{t('Announcement only')}</option>
              </select></label>
              <label className="admin-form-wide">{t('Group title')}<input className="input" value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder={t('Optional for direct messages')} /></label>
              <label className="admin-form-wide">{t('Recipient search')}<input className="input" value={recipientSearch} onChange={(event) => setRecipientSearch(event.target.value)} placeholder={t('Name, email, phone')} /></label>
            </div>
            {(selectedJob || selectedShift) ? (
              <article className="admin-empty-state stack" style={{ alignItems: 'flex-start' }}>
                <strong>{t('Selected context')}</strong>
                {selectedJob ? <span>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job')}: {jobLabel(selectedJob)}</span> : null}
                {selectedShift ? <span>{t('Shift')}: {shiftDateLabel(selectedShift)}{selectedShift.status ? ` . ${t(readableStatus(selectedShift.status))}` : ''}</span> : null}
                {selectedJob?.company_name || selectedJob?.company_org_name ? <span>{t('Client')}: {selectedJob.company_name || selectedJob.company_org_name}</span> : null}
                {selectedShift?.location_name || selectedJob?.location_name ? <span>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}: {selectedShift?.location_name || selectedJob?.location_name}</span> : null}
              </article>
            ) : null}
            <div className="stack" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {recipientsError ? <p className="form-error">{recipientsError}</p> : null}
              {recipientsLoading ? <p className="helper">{industryLabels.explicitIndustry ? t('Loading eligible {workers}...', { workers: industryLabels.label('workers').toLowerCase() }) : t('Loading eligible workers...')}</p> : null}
              {recipients.map((recipient) => (
                <label key={recipient.workerProfileId} className="list-row" style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <input type="checkbox" checked={selectedRecipients.includes(recipient.workerProfileId)} onChange={(event) => {
                    setSelectedRecipients((current) => event.target.checked
                      ? [...current, recipient.workerProfileId]
                      : current.filter((id) => id !== recipient.workerProfileId))
                  }} />
                  <span style={{ overflowWrap: 'anywhere' }}>{recipientLabel(recipient)}</span>
                </label>
              ))}
              {!recipients.length && !recipientsLoading ? <p className="helper">{industryLabels.explicitIndustry ? t('No eligible {workers} found. {Workers} cannot message each other; admins and supervisors can only start scoped conversations.', { workers: industryLabels.label('workers').toLowerCase(), Workers: industryLabels.label('workers') }) : t('No eligible workers found. Workers cannot message each other; admins and supervisors can only start scoped conversations.')}</p> : null}
            </div>
            <label>{t('First message')}<textarea className="input" rows={4} value={firstMessage} onChange={(event) => setFirstMessage(event.target.value)} /></label>
            <button type="button" className="btn btn-primary" disabled={!firstMessage.trim() || sending || !canSendMessages} onClick={createThread}>{t('Create thread')}</button>
          </div>
        </div>
      ) : null}
    </article>
  )
}
